import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NmSegmentControl } from '../../components/NmSegmentControl';
import type { AthleteItem, UserProfile } from '../../services/api';
import { workouts } from '../../services/api';
import { useComparisonSession } from './hooks/useComparisonSession';
import { useComparisonStreams, workoutToSummary, type ComparisonPreviewSegments } from './hooks/useComparisonStreams';
import { ComparisonWorkspace } from './ComparisonWorkspace';
import { BatchComparePanel } from './BatchComparePanel';
import { WorkoutPickerModal } from './WorkoutPickerModal';
import { SaveComparisonModal } from './SaveComparisonModal';
import { SavedComparisonsTab } from './SavedComparisonsTab';
import { AthleteComparisonPanel } from './AthleteComparisonPanel';
import { CoachPeakTrendsPanel } from '../../components/coach/CoachPeakTrendsPanel';
import type { ComparisonPrefill, WorkoutSummary } from './types';

interface ComparisonPageProps {
  user: UserProfile | null;
  coachUserId: number | null;
  defaultAthleteId?: number | null;
  prefill: ComparisonPrefill | null;
  onPrefillConsumed?: () => void;
  athletes?: AthleteItem[];
  isCoach?: boolean;
}

function resolvePickerAthleteId(
  isCoach: boolean,
  athletes: AthleteItem[],
  defaultAthleteId: number | null | undefined,
  coachUserId: number | null,
  existingWorkoutUserId?: number,
): number | null {
  if (existingWorkoutUserId) return existingWorkoutUserId;
  if (isCoach) {
    if (defaultAthleteId && athletes.some(a => a.id === defaultAthleteId)) return defaultAthleteId;
    return athletes[0]?.id ?? null;
  }
  return coachUserId;
}

export function ComparisonPage({
  coachUserId,
  defaultAthleteId = null,
  prefill,
  onPrefillConsumed,
  athletes = [],
  isCoach = false,
}: ComparisonPageProps) {
  const { t } = useTranslation();
  const sessionApi = useComparisonSession(prefill);
  const { session, compareTick, setWorkoutA, setWorkoutB, loadSession } = sessionApi;
  const [previewSegments, setPreviewSegments] = useState<ComparisonPreviewSegments>({ segmentA: null, segmentB: null });
  const [draftSegments, setDraftSegments] = useState<ComparisonPreviewSegments>({ segmentA: null, segmentB: null });

  const displaySegments = useMemo(
    () => ({
      segmentA: previewSegments.segmentA ?? draftSegments.segmentA ?? session.segmentA,
      segmentB: previewSegments.segmentB ?? draftSegments.segmentB ?? session.segmentB,
    }),
    [previewSegments, draftSegments, session.segmentA, session.segmentB],
  );

  const hasPendingSegmentChanges = useMemo(() => {
    const eq = (a: { startSec: number; endSec: number } | null | undefined, b: typeof a) => {
      if (!a && !b) return true;
      if (!a || !b) return false;
      return a.startSec === b.startSec && a.endSec === b.endSec;
    };
    return !eq(draftSegments.segmentA, session.segmentA) || !eq(draftSegments.segmentB, session.segmentB);
  }, [draftSegments, session.segmentA, session.segmentB]);

  const streams = useComparisonStreams(session, coachUserId, compareTick, displaySegments, hasPendingSegmentChanges);

  const [liveMode, setLiveMode] = useState<'pair' | 'batch'>('pair');
  // Coach sidebar → athletes. Calendar/detail workout prefill → workouts.
  // Do not flip back to athletes when prefill is consumed after load.
  const [pageMode, setPageMode] = useState<'athletes' | 'workouts' | 'segments' | 'saved'>(
    isCoach && !prefill?.workoutAId ? 'athletes' : 'workouts',
  );
  const [pickerSlot, setPickerSlot] = useState<'A' | 'B' | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [refreshSaved, setRefreshSaved] = useState(0);

  useEffect(() => {
    if (!prefill?.workoutAId || !coachUserId) return;
    setPageMode('workouts');

    let cancelled = false;
    const { workoutAId, workoutBId, workoutAUserId, workoutBUserId } = prefill;

    void (async () => {
      try {
        const resA = await workouts.getWorkoutDetail(workoutAId);
        if (cancelled) return;
        const wA = resA.data?.data?.workout ?? resA.data?.data;
        if (wA) {
          const userId = workoutAUserId ?? defaultAthleteId ?? coachUserId;
          setWorkoutA(workoutToSummary(wA, userId));
        }

        if (workoutBId) {
          const resB = await workouts.getWorkoutDetail(workoutBId);
          if (cancelled) return;
          const wB = resB.data?.data?.workout ?? resB.data?.data;
          if (wB) {
            const userId = workoutBUserId ?? defaultAthleteId ?? coachUserId;
            setWorkoutB(workoutToSummary(wB, userId));
          }
        }
      } finally {
        if (!cancelled) onPrefillConsumed?.();
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.workoutAId, prefill?.workoutBId, prefill?.workoutAUserId, prefill?.workoutBUserId, coachUserId, defaultAthleteId]);

  const handleSelect = useCallback(
    (w: WorkoutSummary) => {
      if (pickerSlot === 'A') setWorkoutA(w);
      else if (pickerSlot === 'B') setWorkoutB(w);
    },
    [pickerSlot, setWorkoutA, setWorkoutB],
  );

  const pickerAthleteId =
    pickerSlot === 'A'
      ? resolvePickerAthleteId(isCoach, athletes, defaultAthleteId, coachUserId, session.workoutA?.userId)
      : pickerSlot === 'B'
        ? resolvePickerAthleteId(isCoach, athletes, defaultAthleteId, coachUserId, session.workoutB?.userId)
        : null;

  const comparisonOwnerUserId =
    session.workoutA?.userId ?? defaultAthleteId ?? coachUserId;

  const handlePageModeChange = useCallback((nextMode: 'athletes' | 'workouts' | 'segments' | 'saved') => {
    setPageMode(nextMode);
    if (nextMode === 'workouts') {
      sessionApi.setMode('two-workouts');
    } else if (nextMode === 'segments') {
      setLiveMode('pair');
      sessionApi.setMode('one-workout-segments');
    }
  }, [sessionApi]);

  if (!coachUserId) {
    return <div className="cmp-page">{t('compare.noContext')}</div>;
  }

  if (isCoach && athletes.length === 0) {
    return <div className="cmp-page">{t('compare.coachEmpty')}</div>;
  }

  return (
    <div className="cmp-page">
      <header className="cmp-page__header">
        <div className="cmp-page__heading">
          <span>{t('compare.eyebrow')}</span>
          <h1 className="cmp-page__title">
            {isCoach ? t('compare.performanceCenter') : t('compare.titleFull')}
          </h1>
          <p className="cmp-page__hint">
            {isCoach ? t('compare.performanceCenterHint') : t('compare.selfHint')}
          </p>
        </div>
        <NmSegmentControl
          className="cmp-page__mode"
          variant="pills"
          value={pageMode}
          options={[
            ...(isCoach ? [{ value: 'athletes' as const, label: t('compare.modeAthletes') }] : []),
            { value: 'workouts' as const, label: t('compare.modeWorkouts') },
            { value: 'segments' as const, label: t('compare.modeSegments') },
            { value: 'saved' as const, label: t('compare.mySegments') },
          ]}
          onChange={handlePageModeChange}
        />
      </header>

      {pageMode === 'athletes' && isCoach ? (
        <>
          <AthleteComparisonPanel athletes={athletes} defaultAthleteId={defaultAthleteId} />
          <details className="studio-comparison-trends"><summary>{t('studio.comparison.trends')}</summary><CoachPeakTrendsPanel athletes={athletes} refreshKey={0} /></details>
        </>
      ) : pageMode === 'saved' ? (
        <SavedComparisonsTab
          userId={comparisonOwnerUserId ?? coachUserId}
          refreshToken={refreshSaved}
          onOpen={record => {
            loadSession(record.session);
            setDraftSegments({
              segmentA: record.session.segmentA,
              segmentB: record.session.segmentB,
            });
            setPageMode(record.session.mode === 'one-workout-segments' ? 'segments' : 'workouts');
          }}
        />
      ) : (
        <>
          <div className="cmp-page__tabs" style={{ marginBottom: 10 }}>
            <NmSegmentControl
              variant="pills"
              value={liveMode}
              options={[
                { value: 'pair', label: t('compare.liveMode.pair') },
                { value: 'batch', label: t('compare.liveMode.batch') },
              ]}
              onChange={setLiveMode}
            />
          </div>
          {liveMode === 'pair' ? (
            <ComparisonWorkspace
              sessionApi={sessionApi}
              streams={streams}
              draftSegments={draftSegments}
              onDraftSegmentsChange={setDraftSegments}
              onPreviewSegmentsChange={setPreviewSegments}
              onPickA={() => setPickerSlot('A')}
              onPickB={() => setPickerSlot('B')}
              onSave={() => setSaveOpen(true)}
            />
          ) : (
            <BatchComparePanel isCoach={isCoach} athletes={athletes} fallbackUserId={coachUserId} />
          )}
        </>
      )}

      {pickerSlot != null && pickerAthleteId != null && (
        <WorkoutPickerModal
          open
          athleteUserId={pickerAthleteId}
          isCoach={isCoach}
          athletes={athletes}
          slotLabel={pickerSlot}
          excludeId={pickerSlot === 'B' ? session.workoutA?.id : session.workoutB?.id}
          onClose={() => setPickerSlot(null)}
          onSelect={handleSelect}
        />
      )}

      <SaveComparisonModal
        open={saveOpen}
        session={session}
        streams={streams}
        userId={comparisonOwnerUserId ?? coachUserId}
        onClose={() => setSaveOpen(false)}
        onSaved={() => {
          setSaveOpen(false);
          setRefreshSaved(n => n + 1);
        }}
      />
    </div>
  );
}
